import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success("You're in. Welcome.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not register.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="page-register">
      <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
        <form onSubmit={handle} className="w-full max-w-md" data-testid="register-form">
          <h2 className="font-serif text-3xl text-mindora-ink">Begin your space</h2>
          <p className="text-sm text-mindora-muted mt-2">Takes thirty seconds. Costs nothing.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="text-mindora-ink/80 text-xs uppercase tracking-wider">Your name (or a nickname)</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
                placeholder="Riya, or whatever feels true"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-mindora-ink/80 text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
                placeholder="you@somewhere.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-mindora-ink/80 text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
                placeholder="At least 6 characters"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="register-submit-button"
            className="mt-7 w-full btn-primary rounded-full h-11"
          >
            {busy ? "Setting things up…" : "Create my space"}
          </Button>

          <p className="mt-6 text-sm text-mindora-muted text-center">
            Already here?{" "}
            <Link to="/login" className="text-mindora-red hover:underline" data-testid="register-to-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>

      <div className="hidden lg:flex items-center justify-center bg-mindora-panel border-l border-mindora-line p-16 order-1 lg:order-2">
        <div className="max-w-md">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-6">
            <span className="editorial-rule" />a soft beginning
          </div>
          <h1 className="font-serif text-5xl text-mindora-ink leading-tight">
            You can write under your name.<br /><em className="italic text-mindora-red">You can write under none.</em>
          </h1>
          <p className="mt-6 text-mindora-muted leading-relaxed">
            Mindora never displays your email. Stories you publish anonymously can never be traced back to you in public.
            The choice — every single time — is yours.
          </p>
        </div>
      </div>
    </div>
  );
}
